/**
 * Tooltip copy for instruction provider-parameter fields.
 * Keep each string short and plain — shown in a small hover popover.
 */

/** Shared across providers when the field name matches. */
export const COMMON_PARAM_HELP = {
    temperature:
        'How creative vs. predictable the reply is. Low (0–0.3): focused, factual. High (0.8–1.5): varied, creative. Try 0.7 as a default.',
    top_p:
        'Keeps the model picking from the most likely words until their combined probability reaches p. Lower = tighter answers; higher = more variety.',
    top_k:
        'Only consider the K most likely next words. Lower = safer; higher = more word choice per step.',
    max_tokens:
        'Cap on how long the answer can be (in tokens). Higher = longer replies and higher cost.',
    max_new_tokens:
        'Max new tokens IBM watsonx may generate in one reply.',
    min_new_tokens:
        'Min tokens IBM watsonx must generate before it is allowed to stop.',
    stop:
        'Text that ends generation when it appears. Comma-separate multiple stops (e.g. \\n\\n, </END>).',
    stop_sequence:
        'Same as stop — strings that end generation. Comma-separated.',
    stop_sequences:
        'Strings that end generation (Gemini). Up to 5; comma-separated.',
    frequency_penalty:
        'Reduces repeating the same words. Positive values discourage repetition.',
    presence_penalty:
        'Encourages new topics by penalizing words already used. Positive = more variety.',
    repetition_penalty:
        'IBM watsonx: values above 1 discourage repeating the same tokens.',
    seed:
        'Fix randomness for reproducible runs. Same seed + same input → same output. Leave blank for random.',
    random_seed:
        'Mistral: optional seed for reproducible output. Leave blank for random.',
    stream:
        'Stream tokens as they are generated. OpCode LLM steps have their own stream toggle — this mainly affects instruction test runs.',
    n:
        'How many alternative completions to request (Groq / OpenAI-style APIs). Usually 1.',
    response_format:
        'Output shape hint (e.g. JSON). Most chat models use plain text.',
    max_completion_tokens:
        'Cerebras: max tokens in the generated completion.',
    max_output_tokens:
        'Gemini: max tokens in the response. Upper limit depends on the model.',
    candidate_count:
        'Gemini: number of response variants to return. Usually 1.',
};

/** Provider-specific overrides (field key → help string). */
export const PROVIDER_PARAM_HELP = {
    IBM: {
        decoding_method:
            'How the model picks each token. Greedy = always the top choice (deterministic). Sample = weighted random (more natural).',
    },
};

/**
 * Resolve help text for a provider-parameter field.
 * @param {string} provider
 * @param {string} fieldKey
 * @returns {string|null}
 */
export function getProviderParamHelp(provider, fieldKey) {
    const specific = PROVIDER_PARAM_HELP[provider]?.[fieldKey];
    if (specific) return specific;
    if (COMMON_PARAM_HELP[fieldKey]) return COMMON_PARAM_HELP[fieldKey];
    return null;
}

/** Instruction form field help (New / Edit dialog). */
export const INSTRUCTION_FIELD_HELP = {
    name:
        'Unique name in Library → Instructions. LLM steps pick instructions by this name. Cannot be renamed after create.',
    provider:
        'Which API runs this instruction (IBM, Groq, Gemini, …). Controls which parameters appear below. Usually set automatically from the model catalog.',
    model:
        'Exact model id sent to the provider (e.g. ibm/granite-4-h-small). Pick from catalog or type manually.',
    description:
        'Optional note for your team. Not sent to the model.',
    prompt:
        'System prompt / instruction text, sent to the model as-is. The runtime input is the LLM step\'s query (plus image_urls for vision).',
    providerParams:
        'Optional tuning for the provider API: temperature, max tokens, stop sequences, etc. Defaults work for most cases.',
};
